import {
  audioCueIdentifiers,
  type AudioBoundary,
  type AudioCueIdentifier,
  type AudioPlaybackResult,
  type SoundtrackCacheSnapshot,
} from "./audio-contract";
import defaultCueAssetUrl from "../assets/sunlit-garden-audio.json?url&no-inline";
import {
  decodedSoundtrackCacheLimit,
  type SelectedSoundtrackId,
} from "../config/soundtrack-config";
import { selectedSoundtracks } from "./selected-soundtrack";

interface AudioNote {
  readonly durationMs: number;
  readonly frequencyHz: number;
}

interface AudioPhrase {
  readonly gain: number;
  readonly notes: readonly AudioNote[];
}

interface AudioScore {
  readonly cues: Readonly<Record<AudioCueIdentifier, AudioPhrase>>;
  readonly id: "sunlit-garden-audio";
}

type FetchSource = (
  input: RequestInfo | URL,
  init?: RequestInit,
) => Promise<Response>;
type ContextFactory = () => AudioContext;

export interface BrowserAudioBoundaryOptions {
  readonly createContext?: ContextFactory;
  readonly cueAssetUrl?: string;
  readonly fetchSource?: FetchSource;
  readonly musicTracks?: Readonly<
    Record<
      SelectedSoundtrackId,
      { readonly assetUrl: string; readonly playbackGain: number }
    >
  >;
}

const defaultMusicTracks = {
  "chrome-coil": {
    assetUrl: selectedSoundtracks["chrome-coil"].mp3Url,
    playbackGain: selectedSoundtracks["chrome-coil"].playbackGain,
  },
  meteorcoil: {
    assetUrl: selectedSoundtracks.meteorcoil.mp3Url,
    playbackGain: selectedSoundtracks.meteorcoil.playbackGain,
  },
  "sidewinder-stomp": {
    assetUrl: selectedSoundtracks["sidewinder-stomp"].mp3Url,
    playbackGain: selectedSoundtracks["sidewinder-stomp"].playbackGain,
  },
  "crown-of-sparks": {
    assetUrl: selectedSoundtracks["crown-of-sparks"].mp3Url,
    playbackGain: selectedSoundtracks["crown-of-sparks"].playbackGain,
  },
} as const satisfies BrowserAudioBoundaryOptions["musicTracks"];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isReadableSoundtrackResponse = (response: Response): boolean =>
  response.ok || response.status === 0;

const parseNote = (value: unknown): AudioNote => {
  if (!isRecord(value)) {
    throw new Error("Audio note must be an object");
  }
  const durationMs = value.durationMs;
  const frequencyHz = value.frequencyHz;
  if (
    typeof durationMs !== "number" ||
    !Number.isFinite(durationMs) ||
    durationMs <= 0 ||
    typeof frequencyHz !== "number" ||
    !Number.isFinite(frequencyHz) ||
    frequencyHz <= 0
  ) {
    throw new Error("Audio note values must be positive finite numbers");
  }
  return { durationMs, frequencyHz };
};

const parsePhrase = (value: unknown): AudioPhrase => {
  if (!isRecord(value)) {
    throw new Error("Audio phrase must be an object");
  }
  const gain = value.gain;
  const notes = value.notes;
  if (
    typeof gain !== "number" ||
    !Number.isFinite(gain) ||
    gain <= 0 ||
    gain > 1 ||
    !Array.isArray(notes) ||
    notes.length === 0
  ) {
    throw new Error("Audio phrase values are invalid");
  }
  return { gain, notes: notes.map(parseNote) };
};

export const parseAudioScore = (value: unknown): AudioScore => {
  if (!isRecord(value) || value.id !== "sunlit-garden-audio") {
    throw new Error("Audio score identifier is invalid");
  }
  if (!isRecord(value.cues)) {
    throw new Error("Audio score cues are invalid");
  }
  const cues = value.cues;
  return {
    cues: Object.fromEntries(
      audioCueIdentifiers.map((identifier) => [
        identifier,
        parsePhrase(cues[identifier]),
      ]),
    ) as Readonly<Record<AudioCueIdentifier, AudioPhrase>>,
    id: "sunlit-garden-audio",
  };
};

export const renderAudioPhrase = (
  context: AudioContext,
  phrase: AudioPhrase,
): AudioBuffer => {
  const sampleCount = Math.max(
    1,
    Math.round(
      phrase.notes.reduce((total, note) => total + note.durationMs, 0) *
        (context.sampleRate / 1_000),
    ),
  );
  const buffer = context.createBuffer(1, sampleCount, context.sampleRate);
  const channel = buffer.getChannelData(0);
  let offset = 0;
  for (const note of phrase.notes) {
    const noteSamples = Math.round(
      note.durationMs * (context.sampleRate / 1_000),
    );
    for (
      let index = 0;
      index < noteSamples && offset < channel.length;
      index += 1
    ) {
      const progress = index / noteSamples;
      const envelope = Math.sin(Math.PI * progress);
      channel[offset] =
        Math.sin(
          2 * Math.PI * note.frequencyHz * (index / context.sampleRate),
        ) * envelope;
      offset += 1;
    }
  }
  return buffer;
};

export class BrowserAudioBoundary implements AudioBoundary {
  private context: AudioContext | null = null;
  private readonly createContext: ContextFactory;
  private readonly cueAssetUrl: string;
  private readonly decodedMusic = new Map<SelectedSoundtrackId, AudioBuffer>();
  private readonly fetchSource: FetchSource;
  private readonly musicPromises = new Map<
    SelectedSoundtrackId,
    Promise<AudioBuffer>
  >();
  private musicRequestRevision = 0;
  private playingTrackId: SelectedSoundtrackId | null = null;
  private readonly musicTracks: NonNullable<
    BrowserAudioBoundaryOptions["musicTracks"]
  >;
  private musicGain: GainNode | null = null;
  private musicSource: AudioBufferSourceNode | null = null;
  private scorePromise: Promise<AudioScore> | null = null;

  public constructor(options: BrowserAudioBoundaryOptions = {}) {
    this.createContext = options.createContext ?? (() => new AudioContext());
    this.cueAssetUrl = options.cueAssetUrl ?? defaultCueAssetUrl;
    this.fetchSource = options.fetchSource ?? globalThis.fetch.bind(globalThis);
    this.musicTracks = options.musicTracks ?? defaultMusicTracks;
  }

  public destroy(): void {
    this.stopMusic();
    this.decodedMusic.clear();
    this.musicPromises.clear();
    const context = this.context;
    this.context = null;
    if (context !== null) {
      void context.close().catch(() => undefined);
    }
  }

  public getAssetUrl(trackId: SelectedSoundtrackId): string {
    return this.musicTracks[trackId].assetUrl;
  }

  public getSoundtrackCacheSnapshot(): SoundtrackCacheSnapshot {
    return {
      activeLoopCount: this.musicSource === null ? 0 : 1,
      cachedTrackIds: [...this.decodedMusic.keys()],
      configuredDecodedTrackLimit: decodedSoundtrackCacheLimit,
      decodedTrackCount: this.decodedMusic.size,
      pendingDecodeCount: this.musicPromises.size,
    };
  }

  public async playCue(
    identifier: AudioCueIdentifier,
  ): Promise<AudioPlaybackResult> {
    try {
      const context = this.requireContext();
      const score = await this.loadScore();
      if (!(await this.resume(context))) {
        return "blocked";
      }
      const gain = context.createGain();
      gain.gain.value = score.cues[identifier].gain;
      gain.connect(context.destination);
      const source = context.createBufferSource();
      source.buffer = renderAudioPhrase(context, score.cues[identifier]);
      source.connect(gain);
      source.addEventListener(
        "ended",
        () => {
          source.disconnect();
          gain.disconnect();
        },
        { once: true },
      );
      source.start();
      return "played";
    } catch {
      return "unavailable";
    }
  }

  public async startMusic(
    trackId: SelectedSoundtrackId,
  ): Promise<AudioPlaybackResult> {
    const requestRevision = this.musicRequestRevision + 1;
    this.musicRequestRevision = requestRevision;
    try {
      const context = this.requireContext();
      if (!(await this.resume(context))) {
        return "blocked";
      }
      if (requestRevision !== this.musicRequestRevision) {
        return "unavailable";
      }
      if (this.musicSource !== null && this.playingTrackId === trackId) {
        return "played";
      }
      const track = this.musicTracks[trackId];
      const music = await this.loadMusic(context, trackId);
      if (requestRevision !== this.musicRequestRevision) {
        return "unavailable";
      }
      this.retainDecodedMusic(trackId, music);
      const gain = context.createGain();
      gain.gain.value = track.playbackGain;
      gain.connect(context.destination);
      const source = context.createBufferSource();
      source.buffer = music;
      source.loop = true;
      source.connect(gain);
      const previousSource = this.musicSource;
      const previousGain = this.musicGain;
      this.playingTrackId = null;
      this.musicGain = null;
      this.musicSource = null;
      this.releaseSource(previousSource, previousGain);
      try {
        source.start();
      } catch (error) {
        source.disconnect();
        gain.disconnect();
        throw error;
      }
      this.playingTrackId = trackId;
      this.musicGain = gain;
      this.musicSource = source;
      return "played";
    } catch {
      return "unavailable";
    }
  }

  public stopMusic(): void {
    this.musicRequestRevision += 1;
    this.releaseMusicSource();
    this.pruneDecodedMusic();
  }

  private releaseMusicSource(): void {
    const source = this.musicSource;
    const gain = this.musicGain;
    this.playingTrackId = null;
    this.musicSource = null;
    this.musicGain = null;
    this.releaseSource(source, gain);
  }

  private releaseSource(
    source: AudioBufferSourceNode | null,
    gain: GainNode | null,
  ): void {
    if (source !== null) {
      source.stop();
      source.disconnect();
    }
    gain?.disconnect();
  }

  public suspend(): Promise<void> {
    this.stopMusic();
    return this.context?.suspend().catch(() => undefined) ?? Promise.resolve();
  }

  public async unlock(): Promise<boolean> {
    try {
      return await this.resume(this.requireContext());
    } catch {
      return false;
    }
  }

  private loadScore(): Promise<AudioScore> {
    this.scorePromise ??= this.fetchSource(this.cueAssetUrl, {
      credentials: "omit",
    }).then(async (response) => {
      if (!response.ok) {
        throw new Error("Audio score request failed");
      }
      return parseAudioScore(await response.json());
    });
    return this.scorePromise;
  }

  private loadMusic(
    context: AudioContext,
    trackId: SelectedSoundtrackId,
  ): Promise<AudioBuffer> {
    const decoded = this.decodedMusic.get(trackId);
    if (decoded !== undefined) {
      this.decodedMusic.delete(trackId);
      this.decodedMusic.set(trackId, decoded);
      return Promise.resolve(decoded);
    }
    const existing = this.musicPromises.get(trackId);
    if (existing !== undefined) {
      return existing;
    }
    const request = this.fetchSource(this.musicTracks[trackId].assetUrl, {
      credentials: "omit",
    }).then(async (response) => {
      if (!isReadableSoundtrackResponse(response)) {
        throw new Error("Soundtrack request failed");
      }
      return context.decodeAudioData(await response.arrayBuffer());
    });
    this.musicPromises.set(trackId, request);
    void request.then(
      () => {
        if (this.musicPromises.get(trackId) === request) {
          this.musicPromises.delete(trackId);
        }
      },
      () => {
        if (this.musicPromises.get(trackId) === request) {
          this.musicPromises.delete(trackId);
        }
      },
    );
    return request;
  }

  private pruneDecodedMusic(): void {
    const overflow = Math.max(
      0,
      this.decodedMusic.size - decodedSoundtrackCacheLimit,
    );
    for (const oldestTrackId of [...this.decodedMusic.keys()].slice(
      0,
      overflow,
    )) {
      this.decodedMusic.delete(oldestTrackId);
    }
  }

  private retainDecodedMusic(
    trackId: SelectedSoundtrackId,
    music: AudioBuffer,
  ): void {
    this.decodedMusic.delete(trackId);
    this.decodedMusic.set(trackId, music);
    this.pruneDecodedMusic();
  }

  private requireContext(): AudioContext {
    this.context ??= this.createContext();
    return this.context;
  }

  private async resume(context: AudioContext): Promise<boolean> {
    if (context.state !== "running") {
      await context.resume();
    }
    return context.state === "running";
  }
}
